// react
// navigate
import { createSlice } from '@reduxjs/toolkit';
// formik
// @material-ui
// icons
// hooks
// @types
import { AxiosResponseModel } from '../../@types/base';
// redux
import { CONSTS } from './const';
import { dispatch } from '../store';
// routes
// components
// utils
import axios from '../../utils/axios';
// constants
import { HttpStatusCodes } from '../../constants/http-status-codes.enum';
import {
  CameraBetweenDatesListModel,
  CameraResultModel,
  CameraListModel,
  CameraBetweenDatesDetailModel, CameraBetweenDatesDetailPostModel
} from '../../@types/camera';

// ----------------------------------------------------------------------

type CameraState = {
  cameraBetweenDatesError: boolean;
  isCameraBetweenDatesLoading: boolean;
  cameraBetweenDatesList: CameraBetweenDatesListModel[];

  isCameraBetweenDatesDetailLoading: boolean;
  createCameraBetweenDatesModalIsOpen: boolean;
  updateCameraBetweenDatesModalIsOpen: boolean;
  isCameraDetailImageUrlLoading: boolean;
  cameraDetailImageUrlError: boolean;
  cameraDetailImageUrl: string;

  cameraWithoutLocationError: boolean;
  isCameraWithoutLocationLoading: boolean;
  cameraWithoutLocationList: CameraListModel[];
  cameraBetweenDatesDetail: CameraBetweenDatesDetailModel;

  cameraByLocationError: boolean;
  isCameraByLocationLoading: boolean;
  cameraByLocationList: CameraListModel[];

  createCameraModalIsOpen: boolean;
  updateCameraModalIsOpen: boolean;
};

export const cameraBetweenDatesDetailInit: CameraBetweenDatesDetailModel = {
  id: '',
  name: '',
  ipAddress: '',
  aiId: null,
  companyId: 0,
  image: '',
  locationId: null,
  functionType: '',
  modules: []
};

export const cameraBetweenDatesListInit: CameraBetweenDatesListModel = {
  id: '',
  name: '',
  ipAddress: '',
  companyId: 0,
  image: '',
  locationId: undefined,
  createdAt: '',
  updatedAt: '',
  events: [],
  modules: []
};

const initialState: CameraState = {
  cameraBetweenDatesError: false,
  isCameraBetweenDatesLoading: false,
  cameraBetweenDatesList: [],

  isCameraBetweenDatesDetailLoading: false,
  createCameraBetweenDatesModalIsOpen: false,
  updateCameraBetweenDatesModalIsOpen: false,
  cameraDetailImageUrlError: false,
  isCameraDetailImageUrlLoading: false,
  cameraDetailImageUrl: '',

  cameraWithoutLocationError: false,
  isCameraWithoutLocationLoading: false,
  cameraWithoutLocationList: [],
  cameraBetweenDatesDetail: cameraBetweenDatesDetailInit,

  cameraByLocationError: false,
  isCameraByLocationLoading: false,
  cameraByLocationList: [],

  createCameraModalIsOpen: false,
  updateCameraModalIsOpen: false
};

const slice = createSlice({
  name: 'camera',
  initialState,
  reducers: {
    // ----------------------------------------------------------------------
    // CAMERA
    // ----------------------------------------------------------------------

    // START CAMERA WITHOUT LOCATION LOADING
    startCameraWithoutLocationLoading(state) {
      state.isCameraWithoutLocationLoading = true;
    },

    // HAS ERROR CAMERA WITHOUT LOCATION
    hasErrorCameraWithoutLocation(state, action) {
      state.isCameraWithoutLocationLoading = false;
      state.cameraWithoutLocationError = action.payload;
    },

    // GET ALL CAMERAS WITHOUT LOCATION
    getAllCamerasWithoutLocationSuccess(state, action) {
      state.isCameraWithoutLocationLoading = false;
      state.cameraWithoutLocationList = action.payload;
    },

    // START CAMERA BY LOCATION LOADING
    startCameraByLocationLoading(state) {
      state.isCameraByLocationLoading = true;
    },

    // HAS ERROR CAMERA BY LOCATION
    hasErrorCameraByLocation(state, action) {
      state.isCameraByLocationLoading = false;
      state.cameraByLocationError = action.payload;
    },

    // GET ALL CAMERAS BY LOCATION
    getAllCamerasByLocationSuccess(state, action) {
      state.isCameraByLocationLoading = false;
      state.cameraByLocationList = action.payload;
    },

    // ----------------------------------------------------------------------
    // CAMERA BETWEEN DATES
    // ----------------------------------------------------------------------

    // START CAMERA BETWEEN DATES LOADING
    startCameraBetweenDatesLoading(state) {
      state.isCameraBetweenDatesLoading = true;
    },

    // HAS ERROR CAMERA BETWEEN DATES
    hasErrorCameraBetweenDates(state, action) {
      state.isCameraBetweenDatesLoading = false;
      state.cameraBetweenDatesError = action.payload;
    },

    // GET ALL CAMERAS BETWEEN DATES
    getAllCamerasBetweenDatesSuccess(state, action) {
      state.isCameraBetweenDatesLoading = false;
      state.cameraBetweenDatesList = action.payload;
    },

    // START CAMERA BETWEEN DATES LOADING
    startCameraBetweenDatesDetailLoading(state) {
      state.isCameraBetweenDatesDetailLoading = true;
    },

    // GET CAMERA BETWEEN DATES DETAIL
    getCameraBetweenDatesDetailByIdSuccess(state, action) {
      state.isCameraBetweenDatesDetailLoading = false;
      state.cameraBetweenDatesDetail = action.payload;
    },

    startCameraDetailImageUrlLoading(state) {
      state.isCameraDetailImageUrlLoading = true;
    },

    setCameraDetailImageUrlSuccess(state, action) {
      state.isCameraDetailImageUrlLoading = false;
      state.cameraDetailImageUrl = action.payload;
    },

    // HAS ERROR CAMERA WITHOUT LOCATION
    hasErrorCameraDetailImageUrl(state, action) {
      state.isCameraDetailImageUrlLoading = false;
      state.cameraDetailImageUrlError = action.payload;
    },

    /* / CAMERA BETWEEN DATES ADD MODAL OPEN
    onToggleCreateCameraBetweenDatesOpenModal(state, action) {
      state.createCameraBetweenDatesModalIsOpen = action.payload;
    }, */

    // CAMERA BETWEEN DATES DETAIL MODAL OPEN
    onToggleUpdateCameraBetweenDatesOpenModal(state, action) {
      state.updateCameraBetweenDatesModalIsOpen = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { onToggleUpdateCameraBetweenDatesOpenModal } = slice.actions;

// ----------------------------------------------------------------------
// CAMERA

export async function getAllCamerasByLocationWithReturn(locationId: string) {
  dispatch(slice.actions.startCameraByLocationLoading());
  try {
    let request = `${CONSTS.CAMERA_GET_GET_ALL_CAMERAS}`;
    if (locationId) {
      request += `&locationId=${locationId}`;
    }
    const response: AxiosResponseModel<CameraResultModel> = await axios
      .get(request)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getAllCamerasByLocationSuccess(response.data?.data)
      );
    } else {
      dispatch(slice.actions.getAllCamerasByLocationSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCameraByLocation(error));
    return error.response.data;
  }
}

export async function getAllCamerasByCompanyWithReturn(companyId: string) {
  dispatch(slice.actions.startCameraByLocationLoading());
  try {
    let request = `${CONSTS.CAMERA_GET_GET_ALL_CAMERAS}`;
    if (companyId) {
      request += `&companyId=${companyId}`;
    }
    const response: AxiosResponseModel<CameraResultModel> = await axios
      .get(request)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getAllCamerasByLocationSuccess(response.data?.data)
      );
    } else {
      dispatch(slice.actions.getAllCamerasByLocationSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCameraByLocation(error));
    return error.response.data;
  }
}

export async function getAllCamerasWithReturn(
  limit = 100,
  offset = 0
) {
  dispatch(slice.actions.startCameraWithoutLocationLoading());
  try {
    const response: AxiosResponseModel<CameraResultModel> = await axios
      .get(`${CONSTS.CAMERA_GET_GET_ALL_CAMERAS}?&limit=${limit}&offset=${offset}`)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getAllCamerasWithoutLocationSuccess(response.data?.data)
      );
    } else {
      dispatch(slice.actions.getAllCamerasWithoutLocationSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCameraWithoutLocation(error));
    return error.response.data;
  }
}

export async function getAllCamerasBetweenDatesWithReturn(
  startDate: string,
  endDate: string,
  limit?: number,
  offset?: number
) {
  dispatch(slice.actions.startCameraBetweenDatesLoading());
  if (startDate.includes('T')) {
    startDate = startDate.slice(0, startDate.indexOf('T'));
  }
  if (endDate.includes('T')) {
    endDate = endDate.slice(0, endDate.indexOf('T'));
  }
  try {
    const response: AxiosResponseModel<CameraResultModel> = await axios
      .get(
        `${CONSTS.DASHBOARD_GET_GET_ALL_CAMERAS_DASHBOARD}?startDate=${startDate}&endDate=${endDate}&limit=${limit}&offset=${offset}`
      )
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getAllCamerasBetweenDatesSuccess(response.data?.data)
      );
    } else {
      dispatch(slice.actions.getAllCamerasBetweenDatesSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCameraBetweenDates(error));
    return error.response.data;
  }
}

export function getCameraBetweenDatesById(cameraId: string) {
  return async () => {
    dispatch(slice.actions.startCameraBetweenDatesDetailLoading());
    // dispatch(slice.actions.startDetailLoading());
    try {
      const response: AxiosResponseModel<CameraBetweenDatesDetailModel> = await axios
        .get(`${CONSTS.CAMERA_GET_GET_CAMERA}/${cameraId}`)
        .catch((error) => {
          if (error.response) {
            return error.response.data;
          }
          return error;
        });
      if (response && response.status === HttpStatusCodes.StatusSuccess) {
        dispatch(
          slice.actions.getCameraBetweenDatesDetailByIdSuccess(
            response.data?.data || cameraBetweenDatesDetailInit
          )
        );
      } else {
        dispatch(
          slice.actions.getCameraBetweenDatesDetailByIdSuccess(
            cameraBetweenDatesDetailInit
          )
        );
      }
    } catch (error: any) {
      dispatch(slice.actions.hasErrorCameraBetweenDates(error));
    }
  };
}

export function setCameraDetailImageUrl(url: string) {
  return async () => {
    dispatch(slice.actions.startCameraDetailImageUrlLoading());
    try {
      if (url) {
        dispatch(slice.actions.setCameraDetailImageUrlSuccess(url));
      } else {
        dispatch(slice.actions.setCameraDetailImageUrlSuccess(''));
      }
    } catch (error: any) {
      dispatch(slice.actions.hasErrorCameraDetailImageUrl(error));
    }
  };
}

export async function updateCameraWithReturn(
  updateCameraBetweenDates: CameraBetweenDatesDetailPostModel
) {
  try {
    const response: AxiosResponseModel<null> = await axios
      .post(
        `${CONSTS.CAMERA_POST_EDIT_CAMERA}/${updateCameraBetweenDates.id}`,
        updateCameraBetweenDates
      )
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    // if (response && response.statusCode === HttpStatusCodes.StatusSuccess) {
    // dispatch(slice.actions.hasError(false));

    // dispatch(slice.actions.hasError(response.message));
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCameraBetweenDates(error));
    return error.response.data;
  }
}

export async function deleteCamerasWithReturn(cameraIds: string[]) {
  // dispatch(slice.actions.startCompanyLoading());
  try {
    const response: AxiosResponseModel<null> = await axios
      .delete(`${CONSTS.CAMERA_POST_DELETE_CAMERA}/${cameraIds[0]}`)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorCameraBetweenDates(error));
    return error.response.data;
  }
}

// ----------------------------------------------------------------------
